import React from 'react';
import { PageSection, TestimonialsSectionContent } from '../../types/page';
import { Star, Quote } from 'lucide-react';

interface TestimonialsSectionProps {
  section: PageSection;
  className?: string;
}

export const TestimonialsSection: React.FC<TestimonialsSectionProps> = ({ 
  section, 
  className = '' 
}) => {
  const content = section.content as TestimonialsSectionContent;
  
  if (!content.items || content.items.length === 0) {
    return null;
  }

  const backgroundStyle = content.backgroundImage 
    ? { backgroundImage: `url(${content.backgroundImage})` }
    : {};

  const renderStars = (rating: number) => {
    return Array.from({ length: 5 }, (_, index) => (
      <Star
        key={index}
        className={`w-5 h-5 ${
          index < rating 
            ? 'text-yellow-400 fill-yellow-400' 
            : 'text-gray-300'
        }`}
      />
    ));
  };

  return (
    <section 
      className={`relative py-20 bg-cover bg-center bg-no-repeat ${className}`}
      style={backgroundStyle}
    >
      {/* Overlay */}
      {content.backgroundImage && (
        <div className="absolute inset-0 bg-black/70" />
      )}
      
      <div className="relative z-10 container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className={`text-3xl md:text-4xl font-bold mb-4 ${
            content.backgroundImage ? 'text-white' : 'text-gray-900'
          }`}>
            {section.title || content.title || 'Testimonios'}
          </h2>
          {section.subtitle && (
            <p className={`text-xl max-w-2xl mx-auto ${
              content.backgroundImage ? 'text-gray-200' : 'text-gray-600'
            }`}>
              {section.subtitle}
            </p>
          )}
        </div>

        {/* Testimonials Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {content.items.map((testimonial, index) => (
            <div 
              key={index}
              className={`relative p-8 rounded-xl transition-transform hover:scale-105 ${
                content.backgroundImage 
                  ? 'bg-white/10 backdrop-blur-sm border border-white/20' 
                  : 'bg-white shadow-lg border border-gray-100'
              }`}
            >
              {/* Quote Icon */}
              <div className={`absolute top-4 right-4 opacity-20 ${
                content.backgroundImage ? 'text-white' : 'text-gray-400'
              }`}>
                <Quote className="w-8 h-8" />
              </div>

              {/* Rating */}
              <div className="flex items-center mb-4">
                {renderStars(testimonial.rating)}
              </div>

              {/* Comment */}
              <blockquote className={`text-lg mb-6 leading-relaxed ${
                content.backgroundImage ? 'text-gray-200' : 'text-gray-700'
              }`}>
                "{testimonial.comment}"
              </blockquote>

              {/* Author Info */}
              <div className="flex items-center">
                {testimonial.image && (
                  <img
                    src={testimonial.image}
                    alt={testimonial.name}
                    className="w-12 h-12 rounded-full object-cover mr-4"
                  />
                )}
                <div>
                  <div className={`font-semibold ${
                    content.backgroundImage ? 'text-white' : 'text-gray-900'
                  }`}>
                    {testimonial.name}
                  </div>
                  <div className={`text-sm ${
                    content.backgroundImage ? 'text-gray-300' : 'text-gray-500'
                  }`}>
                    {testimonial.role}
                  </div>
                  {testimonial.service && (
                    <div className={`text-xs mt-1 ${
                      content.backgroundImage ? 'text-gray-400' : 'text-gray-400'
                    }`}>
                      Servicio: {testimonial.service}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Trust Indicators */}
        <div className="mt-16 text-center">
          <div className={`text-sm opacity-80 ${
            content.backgroundImage ? 'text-gray-300' : 'text-gray-600'
          }`}>
            Más de {content.items.length} clientes satisfechos confían en nuestros servicios
          </div>
        </div>
      </div>
    </section>
  );
};